// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { BrandCategoriesStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandCategoryModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tooltip, { TooltipPresentationProps } from "@mcmc/MobiusOverrides/Tooltip";
import categoryListStyles from "@mcmc/Overrides/Widgets/Category/CategoryList.style";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import CloudinaryImage from "@mcmc/XDL/Components/Common/CloudinaryImage";
import { transformationsMap } from "@mcmc/XDL/Utilities/cloudinary";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    showImages = "showImages",
    subCategoriesToShow = "subCategoriesToShow",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showImages]: boolean;
        [fields.subCategoriesToShow]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => ({});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandDetailsCategoriesStyles {
    container?: InjectableCss;
    viewMoreLink?: LinkProps;
    subCategoriesLinks?: LinkPresentationProps;
    categoryNameToolTip?: TooltipPresentationProps;
}

export const categoriesStyles: BrandDetailsCategoriesStyles = {
    container: {
        css: css`
            padding-bottom: 30px;
        `,
    },
    viewMoreLink: {
        typographyProps: {
            weight: "bold",
            ellipsis: true,
            size: 15,
        },
        css: css`
            ${getProp("theme.link.defaultProps.css")}
            margin-top: 5px;
            justify-content: center;
        `,
    },
    subCategoriesLinks: {
        color: "text",
        hoverStyle: { color: "text.main", textDecoration: "underline" },
        typographyProps: {
            weight: 400,
            ellipsis: true,
            size: 14,
            lineHeight: "16px",
        },
        css: css`
            ${getProp("theme.link.defaultProps.css")}
            letter-spacing: 0;
            margin-bottom: 8px;
        `,
    },
    categoryNameToolTip: {
        cssOverrides: {
            tooltipContainer: css`
                ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}
                max-width: 200px;
                max-height: 160px;
            `,
        },
    },
};

const BrandDetailsCategories: FC<Props> = ({ fields }) => {
    const [styles] = React.useState(() => mergeToNew(categoryListStyles, categoriesStyles));

    const { isLoading, value: brandCategories } = useContext(BrandCategoriesStateContext);
    if (isLoading || !brandCategories) {
        return null;
    }
    const { showImages, subCategoriesToShow } = fields;

    const renderSubCategoriesLinks = (category: BrandCategoryModel) => {
        const tooltipLinks: any = [];

        category.subCategories?.slice(0, subCategoriesToShow).map(subCategory =>
            tooltipLinks.push(
                <Link
                    key={subCategory.categoryId}
                    href={subCategory.productListPagePath}
                    data-test-selector={`brandCategoriesSubCategoryLink_${subCategory.categoryId}`}
                    {...styles.subCategoriesLinks}
                >
                    {subCategory.categoryName}
                </Link>,
            ),
        );

        if (!(subCategoriesToShow === 0 || (category.subCategories || []).length <= subCategoriesToShow)) {
            tooltipLinks.push(
                <Link
                    href={category.productListPagePath}
                    {...styles.viewMoreLink}
                    aria-label={translate("View More Sub Categories")}
                >
                    {translate("View More")}
                </Link>,
            );
        }

        return tooltipLinks;
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandDetailsCategories">
            {fields.title &&
                createWidgetElement("SectionTitleWidget/SectionTitleWidget", {
                    fields: {
                        title: fields.title,
                    },
                } as any)}
            <StyledWrapper {...styles.categoriesContentWrapper}>
                {brandCategories!.map(category => (
                    <StyledWrapper key={category.categoryId.toString()}>
                        <StyledBox {...styles.styledBox}>
                            <Link
                                href={category.productListPagePath}
                                {...styles.link}
                                aria-label={category.featuredImageAltText}
                            >
                                {showImages && (
                                    <StyledWrapper {...styles.categoryImageWrapper}>
                                        <CloudinaryImage
                                            imageTransformations={transformationsMap.product_sm}
                                            src={category.featuredImagePath}
                                            altText={category.featuredImageAltText}
                                        />
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.categoryNameWrapper}>
                                    {(category.subCategories && category.subCategories?.length > 0) ||
                                    !(
                                        subCategoriesToShow === 0 ||
                                        (category.subCategories || []).length <= subCategoriesToShow
                                    ) ? (
                                        <Tooltip
                                            appendTo={() => document.body}
                                            text={renderSubCategoriesLinks(category)}
                                            triggerAltText={translate("Sub Categories")}
                                            touch={["hold", 200]}
                                            triggerComponent={
                                                <Typography {...styles.categoryName}>
                                                    {category.categoryShortDescription}
                                                </Typography>
                                            }
                                            {...styles.categoryNameToolTip}
                                        ></Tooltip>
                                    ) : (
                                        <Typography {...styles.categoryName}>
                                            {category.categoryShortDescription}
                                        </Typography>
                                    )}
                                </StyledWrapper>
                            </Link>
                        </StyledBox>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandDetailsCategories),
    definition: {
        group: "Brand Details",
        displayName: "Categories",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Shop By Category",
                fieldType: "Translatable",
            },
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.subCategoriesToShow,
                displayName: "Sub Categories To Show",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
